import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { failedResponse, getBackendUrl, loaded, loading, error, LoadStateInfo, notStarted, LoadState } from "../common";
import { ObjectTarget } from "../../app/permissions";
import { ConversationInfo, ConversationMessage, retrieveConversation } from "./conversationSlice";
import { RootState } from "../../app/store";
import { performConversationCreate } from "./conversationCreateSlice";
import { performMessageSend } from "./messageSendSlice";
import { performConversationUpdRead } from "./conversationUpdReadSlice";
import { changeConversationUserStatus } from "./conversationUserSlice";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface RetrieveConversationListRequest {
}

export const retrieveConversationList = createAsyncThunk(
    "retrieveConversationList",
    async (param: RetrieveConversationListRequest, thunkAPI) => {
        console.debug("retrieve conversation list");

        const token = (thunkAPI.getState() as RootState).user.token;

        // const response = await fetch(`${getBackendUrl()}/api/conversation/list/${param.objectTarget}/${param.targetId}`, {
        const response = await fetch(`${getBackendUrl()}/api/conversation/list?targets=${ObjectTarget.SCHOOL},${ObjectTarget.CLASSROOM},${ObjectTarget.DPTO}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${token}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param, { getState }) => {
            const state = getState() as RootState;
            if(! state.user.token) {
                console.debug("No token, not loading conversation list");
                return false;
            }
            if (state.conversationList.state.state === LoadState.NotStarted) {
                // Only load if never loaded before
                return true; 
            }
            return false;
        },
    }
);

export interface ConversationListState {
    state: LoadStateInfo;
    request?: RetrieveConversationListRequest;
    list?: ConversationInfo[];
}

const initialState: ConversationListState = {
    state: notStarted(),
};

export const conversationListSlice = createSlice({
    name: "conversationList",
    initialState,
    reducers: {
        clean: (state) => {
            state.state = notStarted();
            // state.request = undefined;
            // state.list = undefined;
        },
        logout: (state) => {
            state.state = notStarted();
            state.request = undefined;
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveConversationList.pending, (state, action) => {
                state.state = loading();
                state.request = action.meta.arg;
                state.list = undefined;
            })
            .addCase(retrieveConversationList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort(conversationSort);
            })
            .addCase(retrieveConversationList.rejected, (state, action) => {
                state.state = error(action);
            })

            // from other slices
            .addCase(performConversationCreate.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                if (!state.list) {
                    state.list = [];
                }
                const index = state.list.findIndex((c) => c.id == action.payload.id);
                if (index == 0) {
                    return;
                }
                if (index > 0) {
                    state.list.splice(index, 1);
                }
                state.list.unshift(action.payload);
                state.list.sort(conversationSort);
            })
            .addCase(performMessageSend.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                // state.state = loaded();
                const message: ConversationMessage = action.payload;
                console.debug(`Find conversation ${message.conversationId}`);
                const conversation = state.list?.find((c) => c.id == message.conversationId);
                if (conversation) {
                    conversation.lastMessageId = Number(message.id);
                    conversation.lastNonSystemId = Number(message.id);
                    conversation.lastMessageDate = message.processed;
                } else {
                    console.error("Conversation not found", message.conversationId);
                }
                state.list?.sort(conversationSort);
            })
            .addCase(performConversationUpdRead.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const conversationId = action.meta.arg.conversationId;
                // console.debug(`Find conversation ${conversationId}`);
                const conversation = state.list?.find((c) => parseInt(c.id) == conversationId);
                if (conversation) {
                    conversation.lastReadMessageId = action.meta.arg.messageId;
                } else {
                    console.error("Conversation not found", conversationId);
                }
            })

            .addCase(retrieveConversation.pending, (state, action) => {
                console.debug(`${action.type}`);
            })
            .addCase(retrieveConversation.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const conversationId = action.payload.id;
                if(!state.list) {
                    state.list = [];
                }
                const index = state.list.findIndex((c) => c.id == conversationId);
                if (index >= 0) {
                    state.list.splice(index, 1);
                }
                state.list.unshift(action.payload);
                state.list?.sort(conversationSort);
            })
            .addCase(retrieveConversation.rejected, (state, action) => {
                console.debug(`${action.type} :: ${JSON.stringify(action.error)}`);
            })

            .addCase(changeConversationUserStatus.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const request = action.meta.arg;
                if (!state.list) {
                    return ;
                }
                const conversation = state.list.find((c) => c.id == request.conversationId);
                if (conversation) {
                    conversation.conversationUserStatus = request.status;
                }
            });
            
    },
});

// export const { } = conversationListSlice.actions;

export const selectConversationList = (state: RootState) => state.conversationList;

export default conversationListSlice.reducer;


export function conversationSort(a: ConversationInfo, b: ConversationInfo): number {
    // return (b.lastMessageId || 0) - (a.lastMessageId || 0);
    return (b.lastNonSystemId || b.lastMessageId || 0) - (a.lastNonSystemId || a.lastMessageId || 0);
}

